import { useCallback, useEffect } from 'react'
import { CombinedError, useMutation } from 'urql'

import { CreatePaymentIntentDocument } from '@/gql/graphql'
import { useLoggerLegacy } from '@/hooks/useLogger'

export const usePaymentIntent = ({
  totalInCents,
  currency,
  venueId,
  billId,
  lastCompletedPaymentIntentId,
  enabled,
}: {
  totalInCents: number
  currency: string
  venueId: string
  billId?: string | null
  lastCompletedPaymentIntentId?: string | null
  enabled: boolean
}): {
  stripePaymentIntentId?: string | null
  stripePaymentIntentFetching: boolean
  stripePaymentIntentError?: CombinedError
} => {
  const [
    {
      fetching: stripePaymentIntentFetching,
      data,
      error: stripePaymentIntentError,
    },
    createPaymentIntent,
  ] = useMutation(CreatePaymentIntentDocument)

  const { logEventLegacy } = useLoggerLegacy()
  const stripePaymentIntentId = data?.createPaymentIntent.paymentIntentId

  const handlePaymentIntent = useCallback(
    async ({
      billId,
      totalInCents,
      lastCompletedPaymentIntentId,
    }: {
      billId: string
      totalInCents: number
      lastCompletedPaymentIntentId?: string | null
    }) => {
      const { error } = await createPaymentIntent({
        input: {
          amount: totalInCents,
          paymentTargetId: billId,
          currency,
          lastCompletedPaymentIntentId,
          paymentProcessor: 'STRIPE',
          venueId,
        },
      })

      if (error) {
        const simpleError = new Error(
          error.graphQLErrors[0]?.message ?? 'An unexpected error occurred',
        )
        logEventLegacy('Payment Error', {
          context: 'Stripe',
          error: simpleError,
        })
      }
    },
    [createPaymentIntent, currency, venueId, logEventLegacy],
  )

  useEffect(() => {
    if (enabled && billId) {
      void handlePaymentIntent({
        totalInCents,
        billId,
        lastCompletedPaymentIntentId,
      })
    }
  }, [
    enabled,
    handlePaymentIntent,
    totalInCents,
    billId,
    lastCompletedPaymentIntentId,
  ])

  return {
    stripePaymentIntentFetching,
    stripePaymentIntentError,
    stripePaymentIntentId,
  }
}
